import { shopDataUrl, type PublicShop } from "@fusiqo/vozqi-shared";
import { useEffect, useState } from "react";

/**
 * The profile an item's page belongs to: `/t/api/<slug>`, the very
 * read -- and Worker cache entry -- the profile itself starts from, so the
 * page never says anything of the business the profile would not. `none`: an
 * item shared alone, of a business with no published profile (or one in its
 * sample, `publicShopWhere`); `gone` and `failed` leave the page as that.
 */
export type ItemProfile =
  | { status: "none" | "loading" | "gone" | "failed"; shop: null }
  | { status: "ready"; shop: PublicShop };

export function useItemProfile(slug: string | null): ItemProfile {
  const [profile, setProfile] = useState<ItemProfile>({ status: slug === null ? "none" : "loading", shop: null });

  useEffect(() => {
    if (slug === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(shopDataUrl(slug), { signal: controller.signal });
        if (!response.ok) return setProfile({ status: response.status === 404 ? "gone" : "failed", shop: null });
        setProfile({ status: "ready", shop: (await response.json()) as PublicShop });
      } catch {
        if (!controller.signal.aborted) setProfile({ status: "failed", shop: null });
      }
    })();
    return () => controller.abort();
  }, [slug]);

  return profile;
}
